// Bara verticală de unelte de desen, în stânga graficului.
// Click-dreapta pe o unealtă deschide grosimea/transparența; pe Fibonacci — și nivelurile.

import { el, clear } from '../core/util.js';
import { icon } from '../ui/icons.js';
import { settings } from '../core/state.js';
import { TOOLS, PALETTE, DEFAULT_FIB_LEVELS } from './drawings.js';

const COLLAPSE_KEY = 'sx_lw_tb_collapsed';

export class DrawingToolbar {
  /** @param {DrawingLayer} layer */
  constructor(layer, container) {
    this.layer = layer;
    this.container = container;
    this.collapsed = settings.get(COLLAPSE_KEY, false);
    this.buttons = new Map();
    this.#build();

    layer.onToolChange = (tool) => this.#highlight(tool);
    layer.onMagnetChange = (on) => this.magnetBtn.classList.toggle('active', on);
    // După ce o unealtă a terminat un desen, revenim la cursor — ca în ScalpX.
    layer.onToolDone = () => layer.setTool('cursor');
  }

  #build() {
    this.root = el('div', { class: `lwc-tb${this.collapsed ? ' collapsed' : ''}` });

    this.root.append(el('button', {
      class: 'lwc-tb-btn lwc-tb-toggle',
      title: 'Свернуть/развернуть панель',
      onclick: () => this.toggle(),
    }, [icon('chevronRight', 14)]));

    for (const tool of TOOLS) {
      const button = el('button', {
        class: 'lwc-tb-btn',
        title: `${tool.label} (${tool.hotkey}) — правый клик: толщина и прозрачность`,
        onclick: () => this.layer.setTool(tool.id),
        oncontextmenu: (event) => { event.preventDefault(); this.#openStyleMenu(event, tool); },
      }, [icon(tool.icon, 15)]);
      this.buttons.set(tool.id, button);
      this.root.append(button);
    }

    this.magnetBtn = el('button', {
      class: 'lwc-tb-btn lwc-tb-magnet',
      title: 'Магнит: примагничивать к O/H/L/C (G, или держите Ctrl).\nПравый клик — сила магнита',
      onclick: () => this.layer.setMagnet(!this.layer.magnetLocked),
      oncontextmenu: (event) => { event.preventDefault(); this.#openMagnetMenu(event); },
    }, [icon('magnet', 15)]);
    this.root.append(this.magnetBtn);

    this.colorsEl = el('div', { class: 'lwc-tb-colors' });
    for (const color of PALETTE) {
      const swatch = el('button', {
        class: `lwc-tb-color${color === this.layer.color ? ' active' : ''}`,
        style: { background: color },
        onclick: () => {
          this.layer.setColor(color);
          [...this.colorsEl.children].forEach((c) => c.classList.remove('active'));
          swatch.classList.add('active');
        },
      });
      this.colorsEl.append(swatch);
    }
    this.root.append(this.colorsEl);

    this.root.append(
      el('button', {
        class: 'lwc-tb-btn', title: 'Отменить (Ctrl+Z)',
        onclick: () => this.layer.undo(),
      }, [icon('undo', 15)]),
      el('button', {
        class: 'lwc-tb-btn', title: 'Очистить всё',
        onclick: () => { if (confirm('Удалить все рисунки на этом графике?')) this.layer.clearAll(); },
      }, [icon('trash', 15)]),
    );

    this.container.append(this.root);
    this.#highlight('cursor');
  }

  #highlight(tool) {
    for (const [id, button] of this.buttons) button.classList.toggle('active', id === tool);
  }

  toggle() {
    this.collapsed = !this.collapsed;
    this.root.classList.toggle('collapsed', this.collapsed);
    settings.set(COLLAPSE_KEY, this.collapsed);
  }

  // ------------------------------------------------------------- meniuri

  #popup(event, children) {
    this.#closePopup();
    this.popup = el('div', { class: 'lwc-tb-popup' }, children);
    document.body.append(this.popup);
    const rect = event.currentTarget.getBoundingClientRect();
    this.popup.style.left = `${rect.right + 6}px`;
    this.popup.style.top = `${rect.top}px`;
    setTimeout(() => document.addEventListener('mousedown', this.#outside, { once: true }), 0);
  }

  #outside = (event) => {
    if (this.popup && !this.popup.contains(event.target)) this.#closePopup();
  };

  #closePopup() {
    this.popup?.remove();
    this.popup = null;
  }

  #openStyleMenu(event, tool) {
    const rows = [
      el('div', { class: 'lwc-pop-title', text: tool.label }),
      this.#slider('Толщина', 1, 6, 1, this.layer.style.width, (v) => this.layer.setStyle({ width: v })),
      this.#slider('Прозрачность', 0.1, 1, 0.1, this.layer.style.opacity, (v) => this.layer.setStyle({ opacity: v })),
    ];

    if (tool.id === 'fib') {
      rows.push(
        el('div', { class: 'lwc-pop-title', text: 'Уровни Фибоначчи' }),
        el('input', {
          class: 'lwc-pop-input',
          value: this.layer.fibLevels.join(', '),
          onchange: (e) => {
            const levels = e.target.value.split(',')
              .map((x) => Number(x.trim()))
              .filter((x) => Number.isFinite(x));
            this.layer.setFibLevels(levels.length ? levels : DEFAULT_FIB_LEVELS);
          },
        }),
      );
    }

    if (tool.id === 'rr') {
      const group = el('div', { class: 'lwc-pop-group' });
      for (const side of ['long', 'short']) {
        group.append(el('button', {
          class: `lwc-pop-btn${(this.layer.rrSide || 'long') === side ? ' active' : ''}`,
          text: side === 'long' ? 'Long' : 'Short',
          onclick: () => {
            this.layer.setRRSide(side);
            [...group.children].forEach((c) => c.classList.remove('active'));
            group.querySelector(`button:nth-child(${side === 'long' ? 1 : 2})`)?.classList.add('active');
          },
        }));
      }
      rows.push(el('div', { class: 'lwc-pop-title', text: 'Режим' }), group);
    }

    this.#popup(event, rows);
  }

  #openMagnetMenu(event) {
    this.#popup(event, [
      el('div', { class: 'lwc-pop-title', text: 'Сила магнита' }),
      this.#slider('Пиксели', 4, 30, 1, this.layer.magnetPower, (v) => this.layer.setMagnetPower(v)),
    ]);
  }

  #slider(label, min, max, step, value, onInput) {
    const output = el('span', { class: 'lwc-pop-value', text: String(value) });
    return el('label', { class: 'lwc-pop-row' }, [
      el('span', { text: label }),
      el('input', {
        type: 'range', min, max, step, value,
        oninput: (e) => {
          const v = Number(e.target.value);
          output.textContent = String(v);
          onInput(v);
        },
      }),
      output,
    ]);
  }

  destroy() {
    this.#closePopup();
    this.root.remove();
  }
}
